
import { supabase } from '@/integrations/supabase/client';
import { logDbOperation, LogCategory, startMeasure, endMeasure } from '@/utils/logger';

/**
 * Service for managing user preferences
 */
export class UserPreferencesService {
  /**
   * Fetches user preferences
   * @param userId 
   */
  static async fetchUserPreferences(userId: string) {
    const startTime = startMeasure('fetchUserPreferences');
    logDbOperation('SELECT', 'agent_user_preferences', { userId });

    try {
      const { data, error } = await supabase
        .from('agent_user_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle(); // Use maybeSingle instead of single to avoid errors when no row is found

      if (error) {
        logDbOperation('ERROR', 'agent_user_preferences', { error: error.message });
        console.error("Error fetching user preferences:", error);
        throw new Error(`Failed to fetch user preferences: ${error.message}`);
      }

      logDbOperation('SELECT_SUCCESS', 'agent_user_preferences', { 
        found: !!data,
        data: data ? `Preferred Assistant: ${data.preferred_assistant_id}` : 'No preferences'
      });
      
      console.log("User preferences data:", data);
      
      endMeasure(startTime, 'User preferences fetching complete', LogCategory.DATABASE);
      
      return data;
    } catch (err) {
      console.error("Exception in fetchUserPreferences:", err);
      logDbOperation('ERROR', 'agent_user_preferences', { error: err.message });
      throw err; // Re-throw to let the caller handle it
    }
  }

  /**
   * Updates user preferences
   * @param userId 
   * @param preferences 
   */
  static async updateUserPreferences(userId: string, preferences: Partial<Record<string, any>>) {
    const startTime = startMeasure('updateUserPreferences');
    logDbOperation('UPSERT', 'agent_user_preferences', { 
      userId,
      preferences
    });

    try {
      // Convert camelCase properties to snake_case for the database
      const dbPreferences: Record<string, any> = {};
      for (const [key, value] of Object.entries(preferences)) {
        // Convert key from camelCase to snake_case
        const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
        dbPreferences[snakeKey] = value;
      }

      const { data, error } = await supabase
        .from('agent_user_preferences')
        .upsert({ user_id: userId, ...dbPreferences })
        .select()
        .single();

      if (error) {
        logDbOperation('ERROR', 'agent_user_preferences', { error: error.message });
        console.error("Error updating user preferences:", error);
        throw new Error(`Failed to update user preferences: ${error.message}`);
      }

      logDbOperation('UPSERT_SUCCESS', 'agent_user_preferences', { 
        userId,
        preferencesUpdated: Object.keys(preferences).length
      });
      endMeasure(startTime, 'User preferences update complete', LogCategory.DATABASE);
      
      return data;
    } catch (err) {
      console.error("Exception in updateUserPreferences:", err);
      logDbOperation('ERROR', 'agent_user_preferences', { error: err.message });
      throw err; // Re-throw to let the caller handle it
    }
  }
}
